import { graphql } from 'react-relay';

import { transformPreFilteredExtensions } from '@atlassian/forge-ui/web-client/transformForgeExtensions';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import type {
	preloadForgeApps_FetchPreFilteredForgeAppsQuery$data,
	preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables,
} from './__generated__/preloadForgeApps_FetchPreFilteredForgeAppsQuery.graphql.ts';

// Inlined from platform/packages/forge/forge-ui/src/utils/createAtlAttributionHeader.ts
// (not available as a public entry point of @atlassian/forge-ui)
const createAtlAttributionHeader = (workspaceAri?: string): string =>
	JSON.stringify({ atlWorkspaceAri: workspaceAri, service: 'forge-ui' });

export const preloadForgeApps = async ({
	cloudId,
	locale,
	types,
	context,
	includeHidden,
}: preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables): Promise<Extension[] | null> => {
	const { data } = await queryAgg<
		preloadForgeApps_FetchPreFilteredForgeAppsQuery$data,
		preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables
	>({
		query: graphql`
			query preloadForgeApps_FetchPreFilteredForgeAppsQuery(
				$cloudId: ID!
				$context: ConfluenceExtensionRenderingContextInput
				$includeHidden: Boolean
				$locale: String
				$types: [String]
			) {
				confluence_forgeExtensionsByType(
					cloudId: $cloudId
					context: $context
					includeHidden: $includeHidden
					locale: $locale
					types: $types
				) {
					id
					appId
					appVersion
					appOwner
					environmentId
					environmentType
					environmentKey
					consentUrl
					migrationKey
					userAccess {
						enabled
						hasAccess
					}
					key
					properties {
						key
						value
					}
					egress {
						addresses
						category
						type
						inScopeEUD
					}
					scopes
					license {
						active
						capabilitySet
						billingPeriod
						ccpEntitlementId
						ccpEntitlementSlug
						isEvaluation
						subscriptionEndDate
						trialEndDate
						type
					}
					type
					installationId
					remoteInstallationRegion
					hiddenBy
				}
			}
		`,
		// copied from platform/packages/forge/forge-ui/src/web-client/hooks/usePrefilteredExtensionList.ts:createUsePrefilteredExtensionListQueryOptions()
		variables: {
			cloudId,
			context,
			includeHidden: includeHidden ?? false,
			types,
			locale,
		},
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(cloudId),
			},
			allowOnExternalPage: true,
		},
	});

	// @ts-expect-error - FIXME: This error was surfaced after enabling the `typescriptExcludeUndefinedFromNullableUnion`
	// Relay compiler option. Previously, `undefined` was incorrectly included in nullable unions in generated types; this option removes it. The owning team should fix this properly.
	const extensions = (data?.confluence_forgeExtensionsByType || []) as Extension[];
	if (extensions.length > 0) {
		return transformPreFilteredExtensions(extensions);
	}
	return null;
};
